import _ from 'lodash';
import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Icon, Toggle, TextField, TextArea, Button, Box, Flex } from '@vibe/core';
import { AppModal, AppModalContent, AppModalFooter } from '@gorilla/ui/src/components/AppModal';
import { Heading } from '@vibe/core';
import { Email, Alert, Person, Hide, Show, DropdownChevronRight } from '@vibe/icons';
import {
  type Account,
  type AccountUpdate,
  CredentialsAccountUpdateSchema,
  GoogleAccountUpdateSchema,
} from '@gorilla/portal-shared/src/schemas';

type UpdateAccountModalProps = {
  account: Account;
  show: boolean;
  onClose: (accountUpdate?: AccountUpdate) => void;
};

export function UpdateAccountModal({ show, onClose, account }: UpdateAccountModalProps) {
  const defaultValues =
    account.type === 'credentials'
      ? { ..._.pick(account, ['firstname', 'lastname', 'note', 'enabled']), updatePassword: false }
      : { ..._.pick(account, ['note', 'enabled']) };

  const { handleSubmit, control, formState, setError, watch } = useForm<AccountUpdate>({
    resolver: zodResolver(account.type === 'credentials' ? CredentialsAccountUpdateSchema : GoogleAccountUpdateSchema),
    defaultValues,
  });

  const updatePassword = watch('updatePassword');

  const doSubmit = handleSubmit(async (data) => {
    const newAccountUpdate = !('updatePassword' in data && data.updatePassword) ? _.omit(data, 'password') : data;

    try {
      await onClose(newAccountUpdate);
    } catch (err) {
      setError('root.serverError', { message: _.get(err, 'message') || 'An error occurred' });
    }
  });

  const serverError = formState?.errors.root?.serverError;

  const [hidePassword, setHidePassword] = useState<true | false>(true);

  return (
    <AppModal
      show={show}
      onClose={() => {
        onClose();
      }}
      width={400}
      alertDialog={false}
      data-testid="update-account-modal"
    >
      <AppModalContent>
        <Box>
          <Flex gap={12} style={{ marginBottom: '32px' }}>
            <Icon icon={Person} size={22} style={{ transform: 'translate3d(0,-1px,0)' }} />
            <Flex gap={4}>
              <Heading type="h3" weight="normal" align="start" maxLines={1} ellipsis={false}>
                Edit
              </Heading>
              <Icon icon={DropdownChevronRight} size={22} style={{ transform: 'translate3d(0,0,0)' }} />
              <Heading
                type="h3"
                weight="bold"
                align="start"
                maxLines={1}
                ellipsis={true}
                withoutTooltip
                style={{
                  width: '270px',
                }}
              >
                {account.email}
              </Heading>
            </Flex>
          </Flex>
          <form onSubmit={doSubmit}>
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => {
                return (
                  <Flex>
                    <Flex gap={8}>Enable login method</Flex>
                    <Box style={{ marginLeft: 'auto' }}>
                      <Box
                        style={{
                          marginRight: '-8px',
                          marginLeft: '-8px',
                        }}
                      >
                        <Toggle
                          offOverrideText=""
                          onOverrideText=""
                          isSelected={!!field.value}
                          onChange={(value: any) => {
                            field.onChange(value);
                          }}
                          size="small"
                          disabled={formState.isSubmitting}
                        />
                      </Box>
                    </Box>
                  </Flex>
                );
              }}
            />

            {account.type === 'credentials' ? (
              <Controller
                name="updatePassword"
                control={control}
                render={({ field }) => {
                  return (
                    <Flex style={{ marginTop: '8px' }}>
                      <Flex gap={8}>Update password</Flex>
                      <Box style={{ marginLeft: 'auto' }}>
                        <Box
                          style={{
                            marginRight: '-8px',
                            marginLeft: '-8px',
                          }}
                        >
                          <Toggle
                            offOverrideText=""
                            onOverrideText=""
                            isSelected={!!field.value}
                            onChange={(value: any) => {
                              field.onChange(value);
                            }}
                            size="small"
                            disabled={formState.isSubmitting}
                          />
                        </Box>
                      </Box>
                    </Flex>
                  );
                }}
              />
            ) : null}

            {updatePassword && account.type === 'credentials' ? (
              <Controller
                name="password"
                control={control}
                rules={{ required: 'Password is required' }}
                render={({ field }) => {
                  return (
                    <Flex style={{ marginTop: '16px' }}>
                      <TextField
                        id="password"
                        type={hidePassword ? 'password' : 'text'}
                        value={field.value}
                        onChange={field.onChange}
                        size="medium"
                        placeholder="Password"
                        validation={{
                          text: 'Minimum 8 characters, including one number and one symbol.',
                        }}
                        icon={hidePassword ? Show : Hide}
                        onIconClick={() => {
                          setHidePassword((prevHidePassword) => !prevHidePassword);
                        }}
                        autoFocus
                        disabled={formState.isSubmitting}
                      />
                    </Flex>
                  );
                }}
              />
            ) : null}

            {account.type === 'credentials' ? (
              <Flex gap={16} style={{ marginTop: '24px' }}>
                <Controller
                  name="firstname"
                  control={control}
                  rules={{ required: 'First name is required' }}
                  render={({ field }) => {
                    return (
                      <TextField
                        id="firstname"
                        placeholder="First name"
                        value={field.value}
                        onChange={field.onChange}
                        size="medium"
                        autoFocus
                        disabled={formState.isSubmitting}
                      />
                    );
                  }}
                />
                <Controller
                  name="lastname"
                  control={control}
                  rules={{ required: 'Last name is required' }}
                  render={({ field }) => {
                    return (
                      <TextField
                        id="lastname"
                        placeholder="Last name"
                        value={field.value}
                        onChange={field.onChange}
                        size="medium"
                        disabled={formState.isSubmitting}
                      />
                    );
                  }}
                />
              </Flex>
            ) : null}

            {account.type === 'credentials' || account.type === 'google' ? (
              <Flex gap={16} style={{ marginTop: '16px' }}>
                <TextField id="email" placeholder="Email" value={account.email} size="medium" icon={Email} disabled />
              </Flex>
            ) : null}

            <Controller
              name="note"
              control={control}
              render={({ field }) => {
                return (
                  <Flex style={{ marginTop: '24px' }}>
                    <TextArea
                      id="note"
                      label="Comment"
                      //helpText="Add a comment for internal use or reference."
                      value={field.value}
                      onChange={field.onChange}
                      disabled={formState.isSubmitting}
                    />
                  </Flex>
                );
              }}
            />
          </form>
        </Box>
      </AppModalContent>
      <AppModalFooter>
        <Flex gap={12} justify={'end' as any}>
          <Box marginEnd={'auto' as any}>
            {serverError ? (
              <Flex gap={6} style={{ color: '#d83a52' }}>
                <Icon icon={Alert} size={20} />
                <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', fontSize: '14px' }}>
                  {serverError.message}
                </div>
              </Flex>
            ) : (
              <Flex gap={6} style={{ color: '#afb0bd' }}>
                <Icon icon={Email} size={20} />
                <div style={{ fontSize: '14px' }}>Edit login method</div>
              </Flex>
            )}
          </Box>
          <Flex gap={8}>
            <Button
              kind="primary"
              size="medium"
              disabled={!formState.isValid || formState.isSubmitting}
              loading={formState.isSubmitting}
              onClick={() => {
                doSubmit();
              }}
            >
              Update
            </Button>
          </Flex>
        </Flex>
      </AppModalFooter>
    </AppModal>
  );
}
